import { PILLAR_LABEL, PILLAR_WEIGHT, PILLARS, type PillarScores, type Tier } from "@ti/core";
import { cn } from "@/lib/utils";
import { TierBadge } from "./status";

const TIER_BANDS: Array<{ from: number; to: number; tier: Tier }> = [
  { from: 300, to: 550, tier: "T1" },
  { from: 550, to: 700, tier: "T2" },
  { from: 700, to: 850, tier: "T3" },
  { from: 850, to: 1000, tier: "T4" },
];

/** Semi-circular score gauge with tier bands. Range 300–1000 for visual resolution. */
export function ScoreGauge({ value, tier, size = 168, delta, className }: { value: number; tier: Tier; size?: number; delta?: number; className?: string }) {
  const min = 300;
  const max = 1000;
  const r = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  const angle = (v: number) => Math.PI * (1 - (Math.min(max, Math.max(min, v)) - min) / (max - min));
  const pt = (v: number, rad = r) => [cx + rad * Math.cos(angle(v)), cy - rad * Math.sin(angle(v))] as const;
  const arc = (a: number, b: number, rad = r) => {
    const [x1, y1] = pt(a, rad);
    const [x2, y2] = pt(b, rad);
    return `M ${x1} ${y1} A ${rad} ${rad} 0 0 1 ${x2} ${y2}`;
  };
  const [nx, ny] = pt(value, r);
  return (
    <div className={cn("relative inline-flex flex-col items-center", className)} style={{ width: size, height: size / 2 + 40 }}>
      <svg width={size} height={size / 2 + 14} viewBox={`0 0 ${size} ${size / 2 + 14}`} role="img" aria-label={`Agent Reliability Score ${value}, tier ${tier}`}>
        {TIER_BANDS.map((b) => (
          <path key={b.tier} d={arc(b.from + 4, b.to - 4)} stroke={b.tier === tier ? "var(--accent)" : "var(--surface-3)"} strokeWidth={8} fill="none" strokeLinecap="round" opacity={b.tier === tier ? 0.35 : 1} />
        ))}
        <path d={arc(min + 4, value)} stroke="var(--accent)" strokeWidth={8} fill="none" strokeLinecap="round" />
        <circle cx={nx} cy={ny} r={6} fill="var(--surface)" stroke="var(--accent)" strokeWidth={3} />
      </svg>
      <div className="absolute inset-x-0 flex flex-col items-center" style={{ top: size / 2 - 30 }}>
        <span className="text-3xl font-semibold tabular leading-none tracking-tight">{value}</span>
        <div className="mt-1.5 flex items-center gap-1.5">
          <TierBadge tier={tier} />
          {delta !== undefined ? <Delta value={delta} /> : null}
        </div>
      </div>
    </div>
  );
}

/** Signed change. `invert` when an increase is bad (e.g. signal counts). Shown to `digits` decimals (trailing zeros dropped). */
export function Delta({ value, suffix = "", className, invert, digits = 1 }: { value: number; suffix?: string; className?: string; invert?: boolean; digits?: number }) {
  const shown = Number.isFinite(value) ? Number(Math.abs(value).toFixed(digits)) : 0;
  if (shown === 0) return <span className={cn("text-xs text-fg-3 tabular", className)}>±0{suffix}</span>;
  const up = value > 0;
  const good = invert ? !up : up;
  return (
    <span className={cn("text-xs font-medium tabular", good ? "text-good-text" : "text-critical-text", className)}>
      {up ? "▲" : "▼"} {shown.toLocaleString("en-US")}
      {suffix}
    </span>
  );
}

/** A relative change given as a fraction (0.12 = +12%), shown in whole percent. */
export function DeltaPct({ value, suffix = "", ...rest }: { value: number; suffix?: string; className?: string; invert?: boolean }) {
  return <Delta value={value * 100} digits={0} suffix={`%${suffix}`} {...rest} />;
}

export function PillarBars({ pillars, compact }: { pillars: PillarScores; compact?: boolean }) {
  return (
    <div className={cn("flex flex-col", compact ? "gap-1.5" : "gap-2.5")}>
      {PILLARS.map((p) => {
        const v = pillars[p];
        return (
          <div key={p} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
            <span className="truncate text-xs text-fg-2">
              {PILLAR_LABEL[p]} <span className="text-fg-3">· {Math.round(PILLAR_WEIGHT[p] * 100)}%</span>
            </span>
            <span className="text-xs font-medium tabular">{v}</span>
            <div className="col-span-2 h-1.5 overflow-hidden rounded-full bg-surface-3" role="meter" aria-valuenow={v} aria-valuemin={0} aria-valuemax={1000} aria-label={PILLAR_LABEL[p]}>
              <div className="h-full rounded-full" style={{ width: `${v / 10}%`, background: "var(--series-1)" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
